<template>
  <UIcon
    :name="iconName"
    :size="resolvedSize"
    class="leading-none o-icon"
    @click="handleClick"
  />
</template>

<script lang="ts">
export const icons: Record<string, string> = {
  // Arrows & Navigation
  arrowUp: 'lucide:arrow-up',
  arrowDown: 'lucide:arrow-down',
  arrowLeft: 'lucide:arrow-left',
  arrowRight: 'lucide:arrow-right',
  chevronUp: 'lucide:chevron-up',
  chevronDown: 'lucide:chevron-down',
  chevronLeft: 'lucide:chevron-left',
  chevronRight: 'lucide:chevron-right',
  chevronUpDouble: 'lucide:chevrons-up',
  chevronDownDouble: 'lucide:chevrons-down',
  chevronLeftDouble: 'lucide:chevrons-left',
  chevronRightDouble: 'lucide:chevrons-right',

  // Platforms
  apple: 'lucide:apple',
  android: 'lucide:smartphone',
  web: 'lucide:monitor',
  mobile: 'lucide:smartphone',
  facebook: 'lucide:facebook',
  twitter: 'lucide:twitter',
  instagram: 'lucide:instagram',

  // Actions
  add: 'lucide:plus',
  subtract: 'lucide:minus',
  check: 'lucide:check',
  cross: 'lucide:x',
  x: 'lucide:x',
  closePanel: 'lucide:x',
  delete: 'lucide:trash-2',
  remove: 'lucide:trash-2',
  edit: 'lucide:pencil',
  editLink: 'lucide:pencil-line',
  copy: 'lucide:copy',
  save: 'lucide:save',
  undo: 'lucide:undo',
  redo: 'lucide:redo',
  refresh: 'lucide:refresh-cw',
  sync: 'lucide:refresh-cw',
  switch: 'lucide:refresh-cw',
  loading: 'lucide:loader',
  send: 'lucide:send',
  reply: 'lucide:reply',
  share: 'lucide:share-2',
  download: 'lucide:download',
  upload: 'lucide:upload',
  export: 'lucide:share',
  print: 'lucide:printer',
  search: 'lucide:search',
  searchEnter: 'lucide:corner-down-left',
  searchArrowDown: 'lucide:arrow-down',
  searchArrowUp: 'lucide:arrow-up',
  filter: 'lucide:filter',
  drag: 'lucide:move',
  reorder: 'lucide:arrow-up-down',
  reorderHandle: 'lucide:grip-vertical',
  resize: 'lucide:grip-vertical',
  draggable: 'lucide:grip-vertical',
  expand: 'lucide:maximize-2',
  shrink: 'lucide:minimize-2',
  accordion: 'lucide:chevrons-up-down',
  backspace: 'lucide:delete',
  approve: 'lucide:circle-check',
  markRead: 'lucide:mail-check',
  checkDouble: 'lucide:check-check',
  receive: 'lucide:package-check',
  applyStocktake: 'lucide:circle-check',
  connect: 'lucide:link',
  link: 'lucide:link',
  removeLink: 'lucide:unlink',
  unlinked: 'lucide:unlink',
  externalLink: 'lucide:external-link',
  translate: 'lucide:languages',

  // Layout
  list: 'lucide:layout-list',
  grid: 'lucide:layout-grid',
  columns: 'lucide:columns-3',
  moveLeft: 'lucide:panel-left-open',
  moveRight: 'lucide:panel-right-open',
  minimiseGaps: 'lucide:fold-vertical',
  formItems: 'lucide:columns-2',
  hero: 'lucide:panel-top',
  minisiteMenu: 'lucide:panel-left',

  // Status & Alerts
  success: 'lucide:circle-check',
  danger: 'lucide:circle-alert',
  warning: 'lucide:circle-alert',
  info: 'lucide:info',
  successBold: 'lucide:circle-check',
  dangerBold: 'lucide:circle-alert',
  warningBold: 'lucide:circle-alert',
  infoBold: 'lucide:info',
  notification: 'lucide:bell-ring',
  circle: 'lucide:circle',

  // Calendar & Time
  calendar: 'lucide:calendar',
  datepicker: 'lucide:calendar',
  schedule: 'lucide:clock',
  businessHours: 'lucide:clock',
  history: 'lucide:clock-arrow-down',
  reminder: 'lucide:alarm-clock',
  bufferTime: 'lucide:hourglass',
  waitlist: 'lucide:list-ordered',

  // Reservations
  reservation: 'lucide:calendar-clock',
  reservationCancel: 'lucide:calendar-x-2',
  cancel: 'lucide:calendar-x-2',
  reservationCheckIn: 'lucide:user-round-check',
  reservationConfirm: 'lucide:calendar-check-2',
  reservationMove: 'lucide:calendar-arrow-down',
  reservationNoShow: 'lucide:user-round-x',
  reservationPreview: 'lucide:calendar-search',
  reservationRebook: 'lucide:calendar-sync',
  reservationRecurrence: 'lucide:repeat',
  reservationClass: 'lucide:users',

  // Customers
  customer: 'lucide:user',
  customerNew: 'lucide:user-round-plus',
  customerAdd: 'lucide:user-round-plus',
  customerEdit: 'lucide:user-round-pen',
  customerMerge: 'lucide:merge',
  customerResetPass: 'lucide:key-round',
  customerSegments: 'lucide:pie-chart',
  changeCustomer: 'lucide:user-round-cog',
  attendees: 'lucide:users',

  // Users
  user: 'lucide:user',
  users: 'lucide:users',
  employee: 'lucide:user',
  employees: 'lucide:user',

  // Communication
  email: 'lucide:mail',
  sms: 'lucide:message-square-text',
  communications: 'lucide:message-circle',
  message: 'lucide:message-square',
  newChat: 'lucide:square-pen',
  phone: 'lucide:phone',
  homePhone: 'lucide:phone',

  // Sales & Finance
  sale: 'lucide:receipt',
  orderClose: 'lucide:receipt',
  price: 'lucide:wallet',
  dollar: 'lucide:wallet',
  currency: 'lucide:circle-dollar-sign',
  creditCard: 'lucide:credit-card',
  payment: 'lucide:banknote',
  cash: 'lucide:banknote',
  discount: 'lucide:percent',
  charge: 'lucide:tag',
  saleTip: 'lucide:piggy-bank',
  refund: 'lucide:undo-2',
  tax: 'lucide:receipt-text',
  loyaltyPoints: 'lucide:star',
  customerCredit: 'lucide:gauge',
  giftCard: 'lucide:gift',
  pass: 'lucide:ticket',

  // Products & Services
  services: 'lucide:hand-helping',
  product: 'lucide:package',
  resources: 'lucide:wrench',
  productCategory: 'lucide:network',
  serviceCategory: 'lucide:network',
  resourceGroup: 'lucide:network',
  purchaseOrder: 'lucide:clipboard-list',
  supplier: 'lucide:truck',
  stockTake: 'lucide:clipboard-check',

  // Menu Items
  menuDashboard: 'lucide:house',
  menuBookings: 'lucide:clock',
  menuSales: 'lucide:tags',
  menuCustomers: 'lucide:users',
  menuRoster: 'lucide:calendar-days',
  menuMarketing: 'lucide:megaphone',
  menuMessage: 'lucide:message-circle',
  menuReports: 'lucide:chart-no-axes-combined',
  menuManage: 'lucide:settings',
  menuOnlineBooking: 'lucide:globe',
  menuIntegrations: 'lucide:blocks',
  menuNotifications: 'lucide:bell',
  menuAccount: 'lucide:circle-user',
  menuHelp: 'lucide:circle-help',
  menuHamburger: 'lucide:menu',
  menuSearch: 'lucide:search',
  menuMessages: 'lucide:message-square',
  menuAlerts: 'lucide:bell',
  menuSupport: 'lucide:life-buoy',
  menuNotes: 'lucide:sticky-note',
  menuPlan: 'lucide:circle-check',
  menu: 'lucide:menu',

  // Views & UI
  view: 'lucide:eye',
  passwordShow: 'lucide:eye',
  passwordHide: 'lucide:eye-off',
  sortUnset: 'lucide:arrow-down-up',
  sortAsc: 'lucide:arrow-up',
  sortDesc: 'lucide:arrow-down',
  selectTileCheck: 'lucide:circle-check',
  selectTileCircle: 'lucide:circle',
  ellipses: 'lucide:ellipsis',
  placeholder: 'lucide:smile',
  smile: 'lucide:smile',

  // Content & Documents
  notes: 'lucide:sticky-note',
  details: 'lucide:file-text',
  file: 'lucide:files',
  forms: 'lucide:clipboard-check',
  customFields: 'lucide:list',
  template: 'lucide:file-code',
  customPage: 'lucide:file-text',
  attachment: 'lucide:paperclip',
  image: 'lucide:image',
  photos: 'lucide:image',
  gallery: 'lucide:images',
  deleteImage: 'lucide:image-minus',

  // Business & Organization
  company: 'lucide:briefcase',
  building: 'lucide:store',
  address: 'lucide:map-pin',
  location: 'lucide:map-pin',
  chart: 'lucide:bar-chart-3',
  summary: 'lucide:pie-chart',
  marketing: 'lucide:megaphone',
  promote: 'lucide:megaphone',
  roster: 'lucide:calendar-days',
  review: 'lucide:message-square-heart',
  brand: 'lucide:palette',
  statusType: 'lucide:circle-dot',
  preset: 'lucide:sliders-horizontal',

  // Security & Permissions
  locked: 'lucide:lock',
  unlock: 'lucide:lock-open',
  pin: 'lucide:lock',
  permission: 'lucide:key',
  resetPass: 'lucide:key-round',
  login: 'lucide:key',
  logout: 'lucide:log-out',
  closePanels: 'lucide:log-out',
  consent: 'lucide:shield-check',
  policy: 'lucide:clipboard-check',
  variable: 'lucide:variable',

  // Settings & Config
  scheduleOptions: 'lucide:settings',
  preferences: 'lucide:settings',
  allowInternet: 'lucide:globe',
  devices: 'lucide:laptop',
  integrations: 'lucide:puzzle',
  widget: 'lucide:layout-dashboard',
  dataBrowser: 'lucide:database',
  archive: 'lucide:archive',

  // Campaigns
  campaignOpened: 'lucide:mail-open',
  campaignUnopened: 'lucide:mail-x',
  campaignClicked: 'lucide:mouse-pointer-click',
  campaignBounced: 'lucide:alert-triangle',
  campaignComplained: 'lucide:frown',
  campaignUnsubscribed: 'lucide:ban',
  campaignReport: 'lucide:chart-no-axes-combined',

  // Editor
  alignCenter: 'lucide:align-center',
  alignJustify: 'lucide:align-justify',
  alignLeft: 'lucide:align-left',
  alignRight: 'lucide:align-right',
  bold: 'lucide:bold',
  italic: 'lucide:italic',
  underline: 'lucide:underline',
  strikethrough: 'lucide:strikethrough',
  heading: 'lucide:heading',
  h1: 'lucide:heading-1',
  h2: 'lucide:heading-2',
  h3: 'lucide:heading-3',
  paragraph: 'lucide:pilcrow',
  bulletList: 'lucide:list',
  orderedList: 'lucide:list-ordered',
  blockQuote: 'lucide:quote',
  codeBlock: 'lucide:square-code',
  codeLine: 'lucide:code',
  editorUndo: 'lucide:undo',
  editorRedo: 'lucide:redo',
  editorBlocks: 'lucide:blocks',
  table: 'lucide:table',
  toggleHeaderRow: 'lucide:table',
  deleteColumn: 'lucide:table-columns-split',
  deleteRow: 'lucide:table-rows-split',
  addColumnBefore: 'lucide:between-vertical-start',
  addColumnAfter: 'lucide:between-vertical-end',
  addRowAfter: 'lucide:between-horizontal-end',
  addRowBefore: 'lucide:between-horizontal-start',
  toggleCellMerge: 'lucide:table-cells-merge',
  button: 'lucide:plus-square',
  alignImageLeft: 'lucide:align-horizontal-distribute-start',
  alignImageRight: 'lucide:align-horizontal-distribute-end',
  alignImageCenter: 'lucide:align-horizontal-distribute-center',
  at: 'lucide:at-sign',
  braces: 'lucide:braces',

  // Theme
  darkMode: 'lucide:moon',
  lightMode: 'lucide:sun',
  systemMode: 'lucide:laptop',
  theme: 'lucide:palette',
  brush: 'lucide:paintbrush',
  eraser: 'lucide:eraser',
  stamp: 'lucide:stamp',

  // Form Builder
  fieldAnnotation: 'lucide:pen-tool',
  fieldCheckbox: 'lucide:square-check',
  fieldDate: 'lucide:calendar',
  fieldNumber: 'lucide:hash',
  fieldSelectMulti: 'lucide:list',
  fieldSelectOne: 'lucide:list',
  fieldSignature: 'lucide:pen-line',
  fieldText: 'lucide:type',
  fieldTextLong: 'lucide:text',
  fieldToggle: 'lucide:toggle-left',
  layoutLetters: 'lucide:a-large-small',
  layoutParagraph: 'lucide:align-left',
  dragHere: 'lucide:pointer',

  // News & Content
  news: 'lucide:newspaper',
  blog: 'lucide:rss',
  beacon: 'lucide:radio',
  feature: 'lucide:sparkles',
  update: 'lucide:info',
  changelog: 'lucide:list-checks',
  library: 'lucide:library',
  heartHands: 'lucide:heart-handshake',

  // Misc
  botMessage: 'lucide:bot-message-square',
  sparkles: 'lucide:sparkles',
  course: 'lucide:graduation-cap',
  courseRun: 'lucide:play',

  // Search Help Guide
  'hg-rocket-launch': 'lucide:rocket',
  'hg-clock': 'lucide:clock',
  'hg-calendar': 'lucide:calendar',
  'hg-users': 'lucide:users',
  'hg-scissors': 'lucide:scissors',
  'hg-identification-card': 'lucide:id-card',
  'hg-textbox': 'lucide:text-cursor-input',
  'hg-barbell': 'lucide:dumbbell',
  'hg-ticket': 'lucide:ticket',
  'hg-seal-percent': 'lucide:percent',
  'hg-money': 'lucide:banknote',
  'hg-gift': 'lucide:gift',
  'hg-barcode': 'lucide:scan-barcode',
  'hg-bell-ringing': 'lucide:bell-ring',
  'hg-globe': 'lucide:globe',
  'hg-megaphone': 'lucide:megaphone',
  'hg-star': 'lucide:star',
  'hg-plugs': 'lucide:headphones',
}

export default { name: 'OIcon' }
</script>

<script setup lang="ts">
interface Props {
  icon: string | string[]
  size?: string | number
  height?: number | null
  width?: number | null
  fill?: boolean
  stroke?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 16,
  height: null,
  width: null,
  fill: false,
  stroke: 'currentColor',
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const iconName = computed(() => {
  const name = props.icon as string
  if (name.includes(':')) {
    return name
  }
  return icons[name] || icons.placeholder || 'lucide:help-circle'
})

const semanticSizes: Record<string, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 28,
}

const resolvedSize = computed(() => {
  if (typeof props.size === 'number') {
    return props.size
  }

  return semanticSizes[props.size] ?? props.size
})


function handleClick($event: MouseEvent) {
  emit('click', $event)
}
</script>
